import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@clerk/react";
import { toast } from "sonner";
import { useApi, errMsg } from "@/lib/api";

const CMP_KEY = "hc_compare";
const CMP_EVENT = "hc-compare";
export const COMPARE_MAX = 3;

const readCompare = () => {
  try {
    return JSON.parse(localStorage.getItem(CMP_KEY) || "[]").slice(0, COMPARE_MAX);
  } catch {
    return [];
  }
};

export function useCompare() {
  const [ids, setIds] = useState(readCompare);
  useEffect(() => {
    const sync = () => setIds(readCompare());
    window.addEventListener(CMP_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(CMP_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);
  const save = (next) => {
    localStorage.setItem(CMP_KEY, JSON.stringify(next));
    window.dispatchEvent(new Event(CMP_EVENT));
  };
  const toggle = (id) => {
    const cur = readCompare();
    if (cur.includes(id)) return save(cur.filter((x) => x !== id));
    if (cur.length >= COMPARE_MAX) return toast.error(`You can compare up to ${COMPARE_MAX} cars`);
    save([...cur, id]);
  };
  return { ids, toggle, set: (next) => save(next.slice(0, COMPARE_MAX)), clear: () => save([]), has: (id) => ids.includes(id) };
}

let wishCache = null;
const WISH_EVENT = "hc-wishlist";

export function useWishlist() {
  const { isSignedIn, isLoaded } = useAuth();
  const api = useApi();
  const [ids, setIds] = useState(wishCache || []);
  useEffect(() => {
    const sync = () => setIds(wishCache || []);
    window.addEventListener(WISH_EVENT, sync);
    return () => window.removeEventListener(WISH_EVENT, sync);
  }, []);
  useEffect(() => {
    if (!isLoaded) return;
    if (!isSignedIn) {
      wishCache = null;
      setIds([]);
      return;
    }
    if (wishCache) return;
    api.get("/wishlist").then((r) => {
      wishCache = r.data.car_ids;
      window.dispatchEvent(new Event(WISH_EVENT));
    }).catch(() => {});
  }, [isLoaded, isSignedIn, api]);
  const toggle = useCallback(async (id) => {
    if (!isSignedIn) {
      toast("Sign in with your mobile number to save cars");
      const to = `/sign-in?redirect_url=${encodeURIComponent(window.location.href)}`;
      setTimeout(() => { window.location.href = to; }, 1200);
      return;
    }
    const on = (wishCache || []).includes(id);
    try {
      const r = on ? await api.delete(`/wishlist/${id}`) : await api.post(`/wishlist/${id}`);
      wishCache = r.data.car_ids;
      window.dispatchEvent(new Event(WISH_EVENT));
      toast.success(on ? "Removed from wishlist" : "Saved to wishlist");
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, [isSignedIn, api]);
  return { ids, toggle, has: (id) => ids.includes(id), signedIn: isSignedIn };
}
